import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import DashboardCards from "../components/control/DashboardCards";
import CallLogForm from "../components/control/CallLogForm";
import CallPurposeMaster from "../components/control/CallPurposeMaster";
import CallLogFilters from "../components/control/CallLogFilters";
import CallLogsTable from "../components/control/CallLogsTable";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import { getCsrfToken } from "../../../public/api/authApi";
import {
  deleteCallLog,
  exportCallLogs,
  getCallLogById,
  getCallLogMasterData,
  getCallLogs,
  getCallLogsStats,
  patchCallLogFollowUp,
} from "../api/callLogsApi";
import "../styles/callLogsDashboard.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../../memberships/styles/MembershipApplications.css";
import { formatCallDateDisplay } from "../utils/callLogDates";
import { useLiveDashboardStats } from "../../../global/hooks/useLiveDashboardStats";
import { useCallLogRole } from "../utils/useCallLogRole";
import { PHONE_CALL_LOGS_CONTROL_SUBMODULE_SLUG } from "../utils/callLogPermissionSlugs";

const CallLogsControlPage = () => {
  const perms = useCallLogRole(PHONE_CALL_LOGS_CONTROL_SUBMODULE_SLUG);
  const canEditActions = !perms.permissionsLoading && perms.canEdit;
  const canDeleteActions = !perms.permissionsLoading && perms.canDelete;
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [editingLog, setEditingLog] = useState(null);
  const [viewLog, setViewLog] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState(null);

  const [statsReloadToken, setStatsReloadToken] = useState(0);
  const [masterData, setMasterData] = useState({
    types: [],
    purposes: [],
    actions: [],
  });
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    caller_name: "",
    phone: "",
    handled_by: "",
    created_by: "",
    call_type: "",
    purpose_id: "",
    is_call_completed: "",
    start_date: "",
    end_date: "",
    exact_call_date: "",
    call_time: "",
    exact_follow_up_date: "",
    remarks_contains: "",
    action_taken_contains: "",
    call_duration: "",
    page: 1,
    limit: 20,
    sort_by: "call_date",
    sort_dir: "desc",
  });

  /** Wider slice (date range only) so column → Value dropdowns are not limited to the current page. */
  const [filterCatalogLogs, setFilterCatalogLogs] = useState([]);

  const fetchMasterData = useCallback(async () => {
    try {
      const resp = await getCallLogMasterData();
      setMasterData({
        types: resp?.types || [],
        purposes: resp?.purposes || [],
        actions: resp?.actions || [],
      });
    } catch (err) {
      setErrorMsg(err.message || "Failed to load master data");
    }
  }, []);

  const callLogsStatsFetcher = useCallback(
    () =>
      getCallLogsStats({
        start_date: filters.start_date,
        end_date: filters.end_date,
      }),
    [filters.start_date, filters.end_date],
  );

  const { dashboardStats, loadingStats, refreshStats } = useLiveDashboardStats({
    fetcher: callLogsStatsFetcher,
    selectData: (resp) => resp?.data || resp,
    refreshDeps: [statsReloadToken, filters.start_date, filters.end_date],
  });

  const fetchLogsList = useCallback(async () => {
    setLoadingLogs(true);
    setErrorMsg("");
    try {
      const data = await getCallLogs(filters);
      setLogs(data?.data || []);
      if (data?.pagination) setPaginationMeta(data.pagination);
    } catch (err) {
      setErrorMsg(err.message || "Failed to load call logs");
    } finally {
      setLoadingLogs(false);
    }
  }, [filters]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    fetchMasterData();
  }, [fetchMasterData, perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const data = await getCallLogs({
          page: 1,
          limit: 500,
          start_date: filters.start_date,
          end_date: filters.end_date,
        });
        if (!cancelled) {
          setFilterCatalogLogs(data?.data || []);
        }
      } catch {
        if (!cancelled) setFilterCatalogLogs([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [filters.start_date, filters.end_date, perms.permissionsLoading, perms.canView]);

  useEffect(() => {
    if (perms.permissionsLoading || !perms.canView) return;
    const timer = setTimeout(fetchLogsList, 400);
    return () => clearTimeout(timer);
  }, [fetchLogsList, perms.permissionsLoading, perms.canView]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleRequestView = async (id) => {
    try {
      setErrorMsg("");
      const resp = await getCallLogById(id);
      const log = resp?.data || resp;
      if (!log?.id) throw new Error("Unable to load call log details");
      setViewLog(log);
    } catch (err) {
      setErrorMsg(err.message || "Failed to load call log details");
    }
  };

  const handleRequestEdit = async (id) => {
    try {
      setErrorMsg("");
      const resp = await getCallLogById(id);
      const log = resp?.data || resp;
      if (!log?.id) throw new Error("Unable to load call log for editing");
      setEditingLog(log);
      requestAnimationFrame(() => {
        document
          .getElementById("admin-logs-edit")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (err) {
      setErrorMsg(err.message || "Failed to open call log for editing");
    }
  };

  const handleEditSuccess = (msg) => {
    setSuccessMsg(msg || "Call log updated successfully");
    setEditingLog(null);
    fetchLogsList();
    setStatsReloadToken((t) => t + 1);
    refreshStats();
  };

  const handleSortChange = (sortBy, sortDir) => {
    setFilters((prev) => ({ ...prev, sort_by: sortBy, sort_dir: sortDir, page: 1 }));
  };

  const handleFollowUpSave = async (callLogId, followUpDate) => {
    try {
      setErrorMsg("");
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await patchCallLogFollowUp(callLogId, followUpDate, csrfToken);
      setSuccessMsg("Follow-up date updated successfully");
      fetchLogsList();
      setStatsReloadToken((t) => t + 1);
    refreshStats();
    } catch (err) {
      setErrorMsg(err.message || "Failed to update follow-up");
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteCallLog(deleteTarget.id, csrfToken);
      setSuccessMsg("Call log moved to inactive records (soft deleted) successfully");
      setDeleteTarget(null);
      fetchLogsList();
      setStatsReloadToken((t) => t + 1);
    refreshStats();
    } catch (err) {
      setErrorMsg(err.message || "Failed to delete call log");
    } finally {
      setIsDeleting(false);
    }
  };

  const exportFilterPayload = useCallback(() => {
    const { page: _p, limit: _l, sort_by: _sb, sort_dir: _sd, ...rest } = filters;
    return rest;
  }, [filters]);

  const fetchExportRows = useCallback(async () => {
    const data = await exportCallLogs(exportFilterPayload());
    return data?.data || [];
  }, [exportFilterPayload]);

  const toExportRows = (items) =>
    items.map((row, idx) => ({
      "S.No": idx + 1,
      "Log ID": row.id ?? "",
      Date: row.call_date ? formatCallDateDisplay(row.call_date) : "",
      Time: row.call_time || "",
      Caller: row.caller_name || "",
      Phone: row.contact_number || "",
      Type:
        row.call_type === true || row.call_type === 1 || String(row.call_type) === "1"
          ? "Incoming"
          : "Outgoing",
      Purpose: row.purpose_name || "",
      Status: row.status_name || "",
      "Handled By": row.handled_by_name || row.handled_by || "",
      "Created By": row.created_by_name || "",
      Duration: row.call_duration != null ? `${row.call_duration}` : "",
      "Action Taken": row.action_taken || "",
      "Follow-up Date": row.follow_up_date ? formatCallDateDisplay(row.follow_up_date) : "",
      "Follow-up assigned to": (() => {
        const explicit =
          row.follow_up_assigned_to != null && String(row.follow_up_assigned_to).trim() !== "";
        const n = explicit ? row.follow_up_assignee_name : row.handled_by_name;
        const r = explicit ? row.follow_up_assignee_role : row.handled_by_role;
        if (n && r) return `${n} (${r})`;
        return n || "";
      })(),
      Remarks: row.remarks || "",
    }));

  const handleExportCsv = async () => {
    try {
      setErrorMsg("");
      setIsExporting(true);
      const rows = await fetchExportRows();
      if (!rows.length) {
        setErrorMsg("No call logs found for export");
        return;
      }
      const exportRows = toExportRows(rows);
      const headers = Object.keys(exportRows[0]);
      const csv = [
        headers.join(","),
        ...exportRows.map((record) =>
          headers
            .map((key) => {
              const value = String(record[key] ?? "").replace(/"/g, '""');
              return `"${value}"`;
            })
            .join(",")
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      link.download = `call-logs-${y}-${m}-${d}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      setSuccessMsg("Call logs exported to CSV successfully");
    } catch (err) {
      setErrorMsg(err.message || "Failed to export CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setErrorMsg("");
      setIsExporting(true);
      const rows = await fetchExportRows();
      if (!rows.length) {
        setErrorMsg("No call logs found for export");
        return;
      }
      const exportRows = toExportRows(rows);
      const headers = Object.keys(exportRows[0]);
      const html = `
        <table border="1">
          <thead>
            <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
          </thead>
          <tbody>
            ${exportRows
              .map(
                (r) =>
                  `<tr>${headers
                    .map((h) => `<td>${String(r[h] ?? "").replace(/</g, "&lt;")}</td>`)
                    .join("")}</tr>`
              )
              .join("")}
          </tbody>
        </table>
      `;
      const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      link.download = `admin-call-logs-${y}-${m}-${d}.xls`;
      link.click();
      URL.revokeObjectURL(url);
      setSuccessMsg("Call logs exported to Excel successfully");
    } catch (err) {
      setErrorMsg(err.message || "Failed to export Excel");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setErrorMsg("");
      setIsExporting(true);
      const rows = await fetchExportRows();
      if (!rows.length) {
        setErrorMsg("No call logs found for export");
        return;
      }
      const exportRows = toExportRows(rows);
      const headers = Object.keys(exportRows[0]);
      const printWindow = window.open("", "_blank");
      if (!printWindow) throw new Error("Popup blocked. Please allow popups for PDF export.");
      printWindow.document.write(`
        <html><head><title>Admin Call Logs Export</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; }
          h2 { margin: 0 0 12px; }
          table { border-collapse: collapse; width: 100%; font-size: 12px; }
          th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
          th { background: #f3f4f6; }
        </style></head><body>
          <h2>Admin Call Logs</h2>
          <table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
          <tbody>
            ${exportRows
              .map(
                (r) =>
                  `<tr>${headers
                    .map((h) => `<td>${String(r[h] ?? "").replace(/</g, "&lt;")}</td>`)
                    .join("")}</tr>`
              )
              .join("")}
          </tbody></table>
        </body></html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      setSuccessMsg("PDF export opened. Save as PDF from print dialog.");
    } catch (err) {
      setErrorMsg(err.message || "Failed to export PDF");
    } finally {
      setIsExporting(false);
    }
  };

  const quickJumpItems = useMemo(
    () => [
      ...(perms.canView
        ? [
            { key: "stats", label: "Summary", targetId: "admin-logs-stats" },
            { key: "logs", label: "Call Logs", targetId: "admin-logs-workspace" },
          ]
        : []),
      ...(editingLog && canEditActions
        ? [{ key: "edit", label: "Edit Entry", targetId: "admin-logs-edit" }]
        : []),
      ...(perms.canManageMasters
        ? [{ key: "masters", label: "Master tables", targetId: "admin-logs-masters" }]
        : []),
    ],
    [editingLog, perms.canView, perms.canManageMasters, canEditActions],
  );

  if (!perms.permissionsLoading && !perms.canView && !perms.canManageMasters) {
    return <Navigate to="/user-login" replace />;
  }

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">Administrative Area</p>
          <h2 className="user-builder-hero__title">Call Logs Control Center</h2>
          <p className="user-builder-hero__sub">
            Full control over call logs with editing, deletion, assignment, advanced
            filters, and export-ready reporting.
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <GlobalFeedback type="error" text={errorMsg} />
      <GlobalFeedback type="error" text={perms.permissionsError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="schedule-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && perms.canView ? (
          <section id="admin-logs-stats" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Performance Overview</h3>
              <p className="user-builder-panel__hint">Live statistics of phone call logs.</p>
            </div>
            <DashboardCards loading={loadingStats} dashboardStats={dashboardStats} />
          </section>
          ) : null}

          {!perms.permissionsLoading && perms.canView ? (
          <section id="admin-logs-workspace" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Call Logs</h3>
              <p className="user-builder-panel__hint">
                Review records, filter results, export reports, then view, edit, or soft-delete as needed.
              </p>
            </div>
            <CallLogFilters
              filters={filters}
              onFilterChange={setFilters}
              masterData={masterData}
              logs={logs}
              catalogLogs={filterCatalogLogs}
              onColumnVisibilityChange={setColumnVisibility}
              onExportCsv={perms.canExport ? handleExportCsv : undefined}
            />
            {perms.canExport ? (
            <div className="bulk-actions-bar" style={{ marginTop: "0.75rem", marginBottom: "0.75rem" }}>
              <div className="bulk-actions-info">
                <span className="selection-count">Actions</span>
                <span className="selection-hint">Export reports in your preferred format</span>
              </div>
              <div className="bulk-actions-buttons">
                <GlobalButton variant="ghost" onClick={handleExportPdf} disabled={isExporting}>
                  Export PDF
                </GlobalButton>
                <GlobalButton variant="primary" onClick={handleExportExcel} disabled={isExporting}>
                  Export Excel
                </GlobalButton>
              </div>
            </div>
            ) : null}
            <CallLogsTable
              logs={logs}
              loading={loadingLogs}
              masterData={masterData}
              paginationMeta={paginationMeta}
              onPageChange={handlePageChange}
              onView={handleRequestView}
              onEdit={canEditActions ? handleRequestEdit : undefined}
              onDelete={
                canDeleteActions
                  ? (id, callerName) => setDeleteTarget({ id, callerName })
                  : undefined
              }
              sortBy={filters.sort_by}
              sortDir={filters.sort_dir}
              onSortChange={handleSortChange}
              showToolbar={false}
              columnVisibilityOverride={columnVisibility}
            />
          </section>
          ) : null}

          {!perms.permissionsLoading && editingLog && canEditActions ? (
            <section id="admin-logs-edit" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Edit Call Log</h3>
                <p className="user-builder-panel__hint">
                  Update the selected entry using the same inline form flow.
                </p>
              </div>
              <CallLogForm
                mode="edit"
                initialValues={editingLog}
                masterData={masterData}
                onSuccess={handleEditSuccess}
                onCancel={() => setEditingLog(null)}
              />
            </section>
          ) : null}

          {!perms.permissionsLoading && perms.canManageMasters ? (
          <section
            id="admin-logs-masters"
            className="user-builder-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Master Table Management</h3>
              <p className="user-builder-panel__hint">
                Configure core constraints and lookups.
              </p>
            </div>
            <div
              className="admin-masters-unified"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginTop: "2rem",
              }}
            >
              <div id="admin-call-purpose" style={{ scrollMarginTop: "2rem" }}>
                <CallPurposeMaster onDirectoryChange={fetchMasterData} />
              </div>
            </div>
          </section>
          ) : null}
        </div>

        <GlobalQuickJump
          asideAriaLabel="Admin call logs navigation"
          items={quickJumpItems}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal
        open={!!viewLog}
        onClose={() => setViewLog(null)}
        title="Call Log Details"
      >
        {viewLog ? (
          <div
            style={{
              display: "grid",
              gap: "0.65rem",
              padding: "0.1rem 0.1rem 0.8rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                gap: "0.5rem",
                alignItems: "center",
                padding: "0.65rem 0.75rem",
                border: "1px solid #dbeafe",
                borderRadius: "0.75rem",
                background:
                  "linear-gradient(120deg, rgba(239,246,255,0.95) 0%, rgba(255,255,255,1) 68%)",
                boxShadow: "0 6px 18px rgba(15, 23, 42, 0.05)",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 700, letterSpacing: "0.02em" }}>
                  Caller
                </div>
                <div style={{ fontSize: "1rem", color: "#0f172a", fontWeight: 700 }}>
                  {viewLog.caller_name || "—"}
                </div>
              </div>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    padding: "0.25rem 0.5rem",
                    borderRadius: "999px",
                    background:
                      viewLog.call_type === true || viewLog.call_type === 1 || String(viewLog.call_type) === "1"
                        ? "rgba(16,185,129,0.15)"
                        : "rgba(249,115,22,0.15)",
                    color:
                      viewLog.call_type === true || viewLog.call_type === 1 || String(viewLog.call_type) === "1"
                        ? "#047857"
                        : "#c2410c",
                  }}
                >
                  {viewLog.call_type_label || (viewLog.call_type ? "Incoming" : "Outgoing")}
                </span>
                <span
                  style={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    padding: "0.25rem 0.5rem",
                    borderRadius: "999px",
                    background:
                      String(viewLog.is_call_completed) === "1"
                        ? "rgba(34,197,94,0.14)"
                        : "rgba(239,68,68,0.14)",
                    color: String(viewLog.is_call_completed) === "1" ? "#166534" : "#b91c1c",
                  }}
                >
                  {viewLog.status_name || "—"}
                </span>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gap: "0.4rem",
                padding: "0.6rem 0.7rem",
                border: "1px solid #e2e8f0",
                borderRadius: "0.7rem",
                background: "#ffffff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <div style={{ fontSize: "0.76rem", color: "#475569", fontWeight: 700, textTransform: "uppercase" }}>
                Call Information
              </div>
              {[
                ["Log ID", viewLog.id ?? "—"],
                ["Phone", viewLog.contact_number || "—"],
                ["Date", viewLog.call_date ? formatCallDateDisplay(viewLog.call_date) : "—"],
                ["Time", viewLog.call_time || "—"],
                ["Purpose", viewLog.purpose_name || viewLog.purpose_id || "—"],
                ["Duration", viewLog.call_duration != null ? `${viewLog.call_duration} min` : "—"],
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px minmax(0, 1fr)",
                    gap: "0.6rem",
                    alignItems: "start",
                    padding: "0.35rem 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: "0.9rem", color: "#111827", fontWeight: 500 }}>{value}</div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gap: "0.45rem",
                padding: "0.6rem 0.7rem",
                border: "1px solid #e2e8f0",
                borderRadius: "0.7rem",
                background: "#ffffff",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <div style={{ fontSize: "0.76rem", color: "#475569", fontWeight: 700, textTransform: "uppercase" }}>
                Handling & Notes
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px minmax(0, 1fr)",
                  gap: "0.6rem",
                  alignItems: "start",
                }}
              >
                <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>Handled By</div>
                <div style={{ fontSize: "0.9rem", color: "#0f172a", fontWeight: 500 }}>
                  {viewLog.handled_by_role
                    ? `${viewLog.handled_by_name || viewLog.handled_by || "—"} (${viewLog.handled_by_role})`
                    : (viewLog.handled_by_name || viewLog.handled_by || "—")}
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px minmax(0, 1fr)",
                  gap: "0.6rem",
                  alignItems: "start",
                }}
              >
                <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>Follow-up</div>
                <div style={{ fontSize: "0.9rem", color: "#0f172a", lineHeight: 1.55 }}>
                  <div>Date: {viewLog.follow_up_date ? formatCallDateDisplay(viewLog.follow_up_date) : "—"}</div>
                  <div>
                    Assigned to:{" "}
                    {(() => {
                      const explicit =
                        viewLog.follow_up_assigned_to != null &&
                        String(viewLog.follow_up_assigned_to).trim() !== "";
                      const n = explicit ? viewLog.follow_up_assignee_name : viewLog.handled_by_name;
                      const r = explicit ? viewLog.follow_up_assignee_role : viewLog.handled_by_role;
                      if (n && r) return `${n} (${r})`;
                      return n || "—";
                    })()}
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px minmax(0, 1fr)",
                  gap: "0.6rem",
                  alignItems: "start",
                }}
              >
                <div style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>Action Taken</div>
                <div style={{ fontSize: "0.88rem", color: "#0f172a", lineHeight: 1.55 }}>
                  <div>{viewLog.action_taken || "—"}</div>
                  <div style={{ marginTop: "0.4rem", color: "#334155" }}>
                    Remarks: {viewLog.remarks || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </GlobalModal>

      <GlobalModal
        open={!!deleteTarget}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        title="Delete Call Log"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton variant="ghost" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Delete
            </GlobalButton>
          </div>
        }
      >
        <p>
          Are you sure you want to soft delete the call log for{" "}
          <b>{deleteTarget?.callerName || "this caller"}</b>?
        </p>
        <p style={{ marginTop: "0.5rem", color: "#64748b", fontSize: "0.875rem" }}>
          This record will be marked inactive and removed from active lists.
        </p>
      </GlobalModal>
    </div>
  );
};

export default CallLogsControlPage;

/** Caller name rules (shared with CallLogForm) — import from here or from validation module */
export {
  CALLER_NAME_NO_DIGITS_MSG,
  normalizeCallerName,
  validateCallerNameField,
} from "../validation/callerNameValidation";
